import { useCallback, useEffect, useState } from "react";
import type { FormEvent } from "react";
import { ApiError, api } from "@/lib/apiClient";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Dialog } from "@/components/ui/Dialog";
import { hasFormChanges } from "@/components/ui/dialogCloseGuard";
import { Alert } from "@/components/ui/Alert";
import { Checkbox, SelectInput, TextInput, TextareaInput } from "@/components/ui/Field";
import { Spinner } from "@/components/ui/Spinner";
import { Table } from "@/components/ui/Table";

interface ScheduledCall {
  id: number;
  name: string;
  url: string;
  method: string;
  enabled: boolean;
  every_seconds: number;
  headers?: Record<string, string> | null;
  request_timeout_seconds?: number | null;
}

interface ScheduledCallIn {
  name: string;
  url: string;
  method: string;
  enabled: boolean;
  every_seconds: number;
  headers: Record<string, string> | null;
  request_timeout_seconds: number;
}

const EMPTY_FORM = {
  name: "",
  url: "",
  method: "POST",
  every_seconds: "60",
  request_timeout_seconds: "30",
  enabled: true,
  headers: "",
};

type FormState = typeof EMPTY_FORM;

function parseHeaders(raw: string): { headers: Record<string, string> | null; error?: string } {
  const trimmed = raw.trim();
  if (!trimmed) return { headers: null };
  try {
    const parsed = JSON.parse(trimmed);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      const result: Record<string, string> = {};
      for (const [k, v] of Object.entries(parsed)) {
        result[k] = String(v);
      }
      return { headers: result };
    }
    return { headers: null, error: "Headers müssen ein JSON-Objekt sein." };
  } catch (err) {
    return {
      headers: null,
      error: err instanceof Error ? err.message : "Headers sind kein gültiges JSON.",
    };
  }
}

function buildPayload(form: FormState): { payload?: ScheduledCallIn; error?: string } {
  const name = form.name.trim();
  const url = form.url.trim();
  const every = Number(form.every_seconds || 60);
  const method = (form.method || "POST").toUpperCase();
  const timeout = Number(form.request_timeout_seconds || 30);
  const { headers, error } = parseHeaders(form.headers);
  if (error) return { error };
  if (!name || !url || !Number.isFinite(timeout) || timeout <= 0) {
    return { error: "Bitte Name, URL und Timeout (>0) angeben." };
  }
  return {
    payload: {
      name,
      url,
      method,
      enabled: form.enabled,
      every_seconds: every,
      headers,
      request_timeout_seconds: timeout,
    },
  };
}

export function ScheduledCallsSection() {
  const [items, setItems] = useState<ScheduledCall[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [createForm, setCreateForm] = useState<FormState>(EMPTY_FORM);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const [editId, setEditId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<FormState>(EMPTY_FORM);
  const [editBaseline, setEditBaseline] = useState<FormState>(EMPTY_FORM);
  const [editing, setEditing] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.get<ScheduledCall[]>("/admin/scheduled-calls");
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Konnte Jobs nicht laden.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  function openEdit(job: ScheduledCall) {
    setEditId(job.id);
    const initial: FormState = {
      name: job.name,
      url: job.url,
      method: (job.method || "POST").toUpperCase(),
      every_seconds: String(job.every_seconds),
      request_timeout_seconds: String(job.request_timeout_seconds ?? 30),
      enabled: !!job.enabled,
      headers: job.headers ? JSON.stringify(job.headers, null, 2) : "",
    };
    setEditForm(initial);
    setEditBaseline(initial);
    setEditError(null);
  }

  async function onCreate(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setCreateError(null);
    const { payload, error: vErr } = buildPayload(createForm);
    if (!payload) {
      setCreateError(vErr || "Eingaben sind ungültig.");
      return;
    }
    setCreating(true);
    try {
      await api.post<ScheduledCall>("/admin/scheduled-calls", payload);
      setCreateForm(EMPTY_FORM);
      await reload();
    } catch (err) {
      setCreateError(
        err instanceof ApiError ? err.message : err instanceof Error ? err.message : "Speichern fehlgeschlagen.",
      );
    } finally {
      setCreating(false);
    }
  }

  async function onSaveEdit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (editId == null) return;
    setEditError(null);
    const { payload, error: vErr } = buildPayload(editForm);
    if (!payload) {
      setEditError(vErr || "Eingaben sind ungültig.");
      return;
    }
    setEditing(true);
    try {
      await api.put<ScheduledCall>(`/admin/scheduled-calls/${editId}`, payload);
      setEditId(null);
      await reload();
    } catch (err) {
      setEditError(
        err instanceof ApiError ? err.message : err instanceof Error ? err.message : "Aktualisieren fehlgeschlagen.",
      );
    } finally {
      setEditing(false);
    }
  }

  async function runJob(job: ScheduledCall) {
    if (!window.confirm(`Job „${job.name}“ jetzt einmalig ausführen?`)) return;
    try {
      await api.post(`/admin/scheduled-calls/${job.id}/run`);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Ausführen fehlgeschlagen.");
    }
  }

  return (
    <Card
      title="Geplante HTTP-Aufrufe"
      description={
        <>
          Ausführung per systemd-Timer: <code>/admin/run-scheduled-calls</code>
        </>
      }
    >
      {loading && (
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Spinner /> <span>Lädt …</span>
        </div>
      )}
      {!loading && error && (
        <Alert variant="error" title="Fehler">
          {error}
        </Alert>
      )}

      {!loading && !error && (
        <Table<ScheduledCall>
          rowKey={(row) => row.id}
          rows={items}
          stickyEndColumnCount={1}
          emptyText="Noch keine Einträge."
          columns={[
            {
              key: "id",
              header: "#",
              sortable: true,
              sortValue: (r) => r.id,
              render: (r) => r.id,
              className: "w-12",
            },
            {
              key: "name",
              header: "Name",
              sortable: true,
              sortValue: (r) => r.name,
              render: (r) => r.name,
            },
            {
              key: "url",
              header: "URL",
              sortable: true,
              sortValue: (r) => r.url,
              render: (r) => (
                <button
                  type="button"
                  className="max-w-xs truncate text-left text-brand-600 hover:underline dark:text-brand-300"
                  title={r.url}
                  onClick={() => openEdit(r)}
                >
                  {r.url}
                </button>
              ),
            },
            {
              key: "method",
              header: "Methode",
              sortable: true,
              sortValue: (r) => r.method,
              render: (r) => r.method,
            },
            {
              key: "every_seconds",
              header: "Intervall (s)",
              sortable: true,
              sortValue: (r) => r.every_seconds,
              render: (r) => r.every_seconds,
            },
            {
              key: "enabled",
              header: "Aktiv",
              sortable: true,
              sortValue: (r) => r.enabled,
              render: (r) =>
                r.enabled ? (
                  <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-xs font-medium text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200">
                    ✓
                  </span>
                ) : (
                  <span className="rounded bg-slate-200 px-1.5 py-0.5 text-xs text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                    ✗
                  </span>
                ),
            },
            {
              key: "timeout",
              header: "Timeout (s)",
              sortable: true,
              sortValue: (r) => r.request_timeout_seconds ?? -1,
              render: (r) => r.request_timeout_seconds ?? "—",
            },
            {
              key: "actions",
              header: "Aktion",
              headerClassName: "min-w-[18rem]",
              className: "min-w-[18rem]",
              render: (r) => (
                <div className="flex flex-wrap gap-1">
                  <Button size="sm" variant="ghost" onClick={() => openEdit(r)}>
                    Bearbeiten
                  </Button>
                  <Button size="sm" variant="secondary" onClick={() => runJob(r)}>
                    Jetzt ausführen
                  </Button>
                </div>
              ),
            },
          ]}
        />
      )}

      <h3 className="mt-6 text-sm font-semibold text-slate-800 dark:text-slate-200">
        Neuen Job hinzufügen
      </h3>
      <form onSubmit={onCreate} className="mt-2 grid grid-cols-1 gap-3 md:grid-cols-12">
        <TextInput
          id="sc-name"
          label="Name"
          required
          className="md:col-span-3"
          value={createForm.name}
          onChange={(e) => setCreateForm((s) => ({ ...s, name: e.target.value }))}
        />
        <TextInput
          id="sc-url"
          label="URL"
          required
          type="url"
          className="md:col-span-5"
          placeholder="https://dev.api.ernsthaus.de/hero/custom-fields/sync"
          value={createForm.url}
          onChange={(e) => setCreateForm((s) => ({ ...s, url: e.target.value }))}
        />
        <TextInput
          id="sc-every"
          label="Intervall (s)"
          type="number"
          min={1}
          required
          className="md:col-span-2"
          value={createForm.every_seconds}
          onChange={(e) => setCreateForm((s) => ({ ...s, every_seconds: e.target.value }))}
        />
        <SelectInput
          id="sc-method"
          label="Methode"
          className="md:col-span-1"
          value={createForm.method}
          onChange={(e) => setCreateForm((s) => ({ ...s, method: e.target.value }))}
        >
          <option value="GET">GET</option>
          <option value="POST">POST</option>
        </SelectInput>
        <TextInput
          id="sc-timeout"
          label="Timeout (s)"
          type="number"
          step="0.1"
          min={0.1}
          required
          className="md:col-span-1"
          value={createForm.request_timeout_seconds}
          onChange={(e) =>
            setCreateForm((s) => ({ ...s, request_timeout_seconds: e.target.value }))
          }
        />
        <div className="flex items-end md:col-span-12">
          <Checkbox
            id="sc-enabled"
            label="Aktiv"
            checked={createForm.enabled}
            onChange={(e) => setCreateForm((s) => ({ ...s, enabled: e.target.checked }))}
          />
        </div>
        <TextareaInput
          id="sc-headers"
          label="Headers (JSON, optional)"
          className="md:col-span-12"
          rows={2}
          placeholder='{"X-Cron-Secret":"${NACHWEISE_CRON_SECRET}"}'
          value={createForm.headers}
          onChange={(e) => setCreateForm((s) => ({ ...s, headers: e.target.value }))}
          hint="Tipp: ${ENV_VAR} wird beim Ausführen aus der .env gezogen."
        />
        <div className="md:col-span-12">
          {createError && (
            <div className="mb-2">
              <Alert variant="error">{createError}</Alert>
            </div>
          )}
          <Button type="submit" loading={creating}>
            Job hinzufügen
          </Button>
        </div>
      </form>

      <Dialog
        open={editId != null}
        onClose={() => setEditId(null)}
        dirty={hasFormChanges(editForm, editBaseline)}
        title="HTTP-Aufruf bearbeiten"
        size="lg"
        footer={
          <>
            <Button variant="secondary" onClick={() => setEditId(null)}>
              Abbrechen
            </Button>
            <Button form="sc-edit-form" type="submit" loading={editing}>
              Speichern
            </Button>
          </>
        }
      >
        <form id="sc-edit-form" onSubmit={onSaveEdit} className="space-y-3">
          <TextInput
            label="Name"
            required
            value={editForm.name}
            onChange={(e) => setEditForm((s) => ({ ...s, name: e.target.value }))}
          />
          <TextInput
            label="URL"
            type="url"
            required
            value={editForm.url}
            onChange={(e) => setEditForm((s) => ({ ...s, url: e.target.value }))}
          />
          <TextareaInput
            label="Headers (JSON, optional)"
            rows={2}
            value={editForm.headers}
            onChange={(e) => setEditForm((s) => ({ ...s, headers: e.target.value }))}
          />
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <TextInput
              label="Intervall (s)"
              type="number"
              min={1}
              required
              value={editForm.every_seconds}
              onChange={(e) => setEditForm((s) => ({ ...s, every_seconds: e.target.value }))}
            />
            <SelectInput
              label="Methode"
              value={editForm.method}
              onChange={(e) => setEditForm((s) => ({ ...s, method: e.target.value }))}
            >
              <option value="GET">GET</option>
              <option value="POST">POST</option>
            </SelectInput>
            <TextInput
              label="Timeout (s)"
              type="number"
              step="0.1"
              min={0.1}
              required
              value={editForm.request_timeout_seconds}
              onChange={(e) =>
                setEditForm((s) => ({ ...s, request_timeout_seconds: e.target.value }))
              }
            />
          </div>
          <Checkbox
            label="Aktiv"
            checked={editForm.enabled}
            onChange={(e) => setEditForm((s) => ({ ...s, enabled: e.target.checked }))}
          />
          {editError && <Alert variant="error">{editError}</Alert>}
        </form>
      </Dialog>
    </Card>
  );
}
